import { myEnergySelectors } from 'ducks/myEnergy'
import { paymentOptionSelectionSelectors } from 'ducks/paymentOptionSelection'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { StudioSystemType } from 'types/global'
import { PaymentOptionDataType } from 'types/paymentOptions'
import { ChangeOrderType, ProjectType } from 'types/projects'
import { isCompletedChangeOrder } from 'util/changeOrder'
import { urlToId } from 'util/misc'

export interface EffectiveCashflowContext {
  system: StudioSystemType | undefined
  paymentOption: PaymentOptionDataType | undefined
  systemUuid: string | undefined
  paymentOptionId: number | undefined
  orgId: number | undefined
  isSold: boolean
  latestCompletedChangeOrder: ChangeOrderType | undefined
}

const getLatestCompletedChangeOrder = (
  changeOrders: ChangeOrderType[] | undefined
): ChangeOrderType | undefined => {
  const completed = (changeOrders || []).filter(isCompletedChangeOrder)
  if (!completed.length) return undefined
  return [...completed].sort((a, b) => {
    const dateA = new Date(a.created_date).getTime()
    const dateB = new Date(b.created_date).getTime()
    if (dateA !== dateB) return dateB - dateA
    return b.id - a.id
  })[0]
}

/**
 * Studio holds calculated payment-option data (pricing, milestones). Project API systems
 * are the source of truth for sold URLs/ids but may lag or omit calcs — match pre-refactor
 * behaviour by preferring editor payment options when the studio is loaded.
 */
const resolvePaymentOption = (
  system: StudioSystemType | undefined,
  paymentOptionId: number | undefined
): PaymentOptionDataType | undefined => {
  if (!system || !paymentOptionId) return undefined

  const editorSystem = window.editor?.getSystems()?.find((s) => s.uuid === system.uuid)
  const fromEditor = editorSystem?.payment_options?.find((pmt) => pmt.id === paymentOptionId)
  if (fromEditor) return fromEditor

  return system.payment_options?.find((pmt) => pmt.id === paymentOptionId)
}

/**
 * Single source of truth for operational cashflow after V3 change orders.
 * Resolves the sold system and the payment option id used for milestone collection
 * (latest completed CO payment_option_sold when present, else project baseline).
 */
export const useEffectiveCashflowContext = (
  project: Partial<ProjectType> | undefined
): EffectiveCashflowContext => {
  const justSoldSystemUuid = useSelector(myEnergySelectors.getSystemUuidForcedMarkedAsSold)
  const refreshSystemsTrigger = useSelector(paymentOptionSelectionSelectors.getSystemRefreshTrigger)

  return useMemo(() => {
    const soldSystemURL = project?.system_sold
    const baselinePaymentOptionSoldUrl = project?.payment_option_sold
    const isSold = Boolean(soldSystemURL && baselinePaymentOptionSoldUrl)
    const latestCompletedChangeOrder = getLatestCompletedChangeOrder(project?.change_orders)

    let system: StudioSystemType | undefined
    if (soldSystemURL) {
      system = project?.systems?.find((sys) => sys.url === soldSystemURL) as StudioSystemType | undefined
    }
    if (!system && justSoldSystemUuid) {
      system = project?.systems?.find((sys) => sys.uuid === justSoldSystemUuid) as StudioSystemType | undefined
    }

    let paymentOptionId: number | undefined
    if (latestCompletedChangeOrder?.payment_option_sold) {
      paymentOptionId = latestCompletedChangeOrder.payment_option_sold
    } else if (baselinePaymentOptionSoldUrl) {
      paymentOptionId = urlToId(baselinePaymentOptionSoldUrl)
    }

    const paymentOption = resolvePaymentOption(system, paymentOptionId)

    return {
      system,
      paymentOption,
      systemUuid: system?.uuid ?? justSoldSystemUuid,
      paymentOptionId,
      orgId: paymentOption?.org_id,
      isSold,
      latestCompletedChangeOrder,
    }
  }, [
    project?.system_sold,
    project?.payment_option_sold,
    project?.systems,
    project?.change_orders,
    justSoldSystemUuid,
    refreshSystemsTrigger,
  ])
}
